"use client";

import { useEffect } from "react";

/** Client behaviours: TOC highlight, copy buttons, lightbox, hash-open details. */
export function PostInteractions() {
  useEffect(() => {
    const $$ = <T extends Element>(sel: string, root: ParentNode = document) =>
      Array.from(root.querySelectorAll<T>(sel));

    const links = $$<HTMLAnchorElement>(".toc-rail [data-toc]");
    const heads = links.map((a) => document.getElementById(a.dataset.toc || ""));

    const cur = () => {
      let id: string | null = null;
      // Match sticky header (~72px) + a little breathing room
      const threshold = 100;
      heads.forEach((h) => {
        if (h && h.getBoundingClientRect().top < threshold) id = h.id;
      });
      links.forEach((a) => {
        a.setAttribute("aria-current", a.dataset.toc === id ? "true" : "false");
      });
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          cur();
          ticking = false;
        });
      }
    };
    cur();
    window.addEventListener("scroll", onScroll, { passive: true });

    $$<HTMLAnchorElement>(".toc-box a").forEach((a) => {
      a.addEventListener("click", () => {
        const details = a.closest("details");
        if (details) details.open = false;
      });
    });

    const copy = (txt: string, btn: HTMLElement, done: string) => {
      const write = navigator.clipboard
        ? navigator.clipboard.writeText(txt)
        : Promise.reject();
      write.then(
        () => {
          const o = btn.textContent;
          btn.textContent = done;
          setTimeout(() => {
            btn.textContent = o;
          }, 1600);
        },
        () => {
          btn.textContent = "Press Ctrl+C";
        }
      );
    };

    const onCopyClick = (e: Event) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-copy]");
      if (!btn) return;
      const code = btn.closest(".codeblock")?.querySelector("code");
      if (code) copy(code.textContent || "", btn, "Copied");
    };

    const onCalcCopy = (e: Event) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-copy-calc]");
      if (!btn) return;
      const rows = $$<HTMLElement>(".calc-row", btn.parentElement || document);
      copy(
        rows.map((p) => p.innerText).join("\n"),
        btn,
        "Copied"
      );
    };

    const onLinkCopy = (e: Event) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-copy-link]");
      if (!btn) return;
      copy(btn.dataset.copyLink || window.location.href, btn, "Link copied");
    };

    document.addEventListener("click", onCopyClick);
    document.addEventListener("click", onCalcCopy);
    document.addEventListener("click", onLinkCopy);

    const lb = document.querySelector<HTMLDialogElement>(".iv-post .lightbox");
    const li = lb?.querySelector("img");
    let last: HTMLElement | null = null;

    const onZoom = (e: Event) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-zoom]");
      if (!btn || !lb || !li) return;
      last = btn;
      const img = btn.querySelector("img");
      if (img) {
        li.src = img.currentSrc || img.src;
        li.alt = img.alt;
      }
      lb.showModal();
    };

    document.addEventListener("click", onZoom);

    const closeLb = () => lb?.close();
    lb?.querySelector(".lb-close")?.addEventListener("click", closeLb);
    lb?.addEventListener("click", (e) => {
      if (e.target === lb) closeLb();
    });
    lb?.addEventListener("close", () => last?.focus());

    const openHash = () => {
      const hash = location.hash?.slice(1);
      if (!hash) return;
      const t = document.getElementById(hash);
      if (t && t.tagName === "DETAILS") (t as HTMLDetailsElement).open = true;
    };
    window.addEventListener("hashchange", openHash);
    openHash();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("hashchange", openHash);
      document.removeEventListener("click", onCopyClick);
      document.removeEventListener("click", onCalcCopy);
      document.removeEventListener("click", onLinkCopy);
      document.removeEventListener("click", onZoom);
    };
  }, []);

  return (
    <dialog className="lightbox" aria-label="Enlarged figure">
      <button type="button" className="lb-close" aria-label="Close">
        ×
      </button>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img alt="" />
    </dialog>
  );
}
